import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  DollarSign, Gauge, Users, Wrench, Loader2, RefreshCw, ShieldCheck,
  UserPlus, Trash2, Power, Zap, Minus, Plus, X,
} from "lucide-react";
import { adminApi } from "@/api/admin";

const TABS = [
  { k: "payouts", label: "PAYOUTS", Icon: DollarSign },
  { k: "engine", label: "ENGINE", Icon: Gauge },
  { k: "config", label: "CONFIG", Icon: Users },
  { k: "tools", label: "TOOLS", Icon: Wrench },
];

const Empty = ({ label }) => (
  <div className="py-10 grid place-items-center" data-testid="business-empty-state">
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-charcoal/40">{label}</p>
  </div>
);

/* ------------------------------- PAYOUTS -------------------------------- */
function PayoutsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => { setBusy(true); adminApi.payouts().then(setData).catch(() => setData({ payouts: [] })).finally(() => setBusy(false)); };
  useEffect(() => { load(); }, []);
  const rows = data?.payouts || [];
  return (
    <div data-testid="business-payouts-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50">Creator Billing Logs</p>
        <button onClick={load} data-testid="payouts-refresh" className="w-7 h-7 grid place-items-center rounded-lg bg-cream border-2 border-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">{busy ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}</button>
      </div>
      {rows.length === 0 ? <Empty label="No pending payouts" /> : (
        <div className="space-y-2">
          {rows.map((p, i) => (
            <div key={p.id || i} className="flex items-center gap-2 border-2 border-charcoal/15 rounded-xl bg-cream p-2.5" data-testid={`payout-row-${i}`}>
              <div className="min-w-0 flex-1">
                <p className="font-mono text-[11px] font-black text-charcoal truncate">{p.creator || "—"}</p>
                <p className="font-mono text-[8px] uppercase text-charcoal/40">{p.status}</p>
              </div>
              <span className="font-serif text-lg text-charcoal tabular-nums">${Number(p.amount || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- ENGINE -------------------------------- */
const POOL_META = {
  openai: { label: "OpenAI Balance", unit: "usd" },
  fal: { label: "Fal.ai Credits", unit: "usd" },
  elevenlabs: { label: "ElevenLabs Characters", unit: "usd" },
};
function EnginePanel() {
  const [pools, setPools] = useState(null);
  useEffect(() => { adminApi.apiBudget().then((d) => setPools(d.pools || [])).catch(() => setPools([])); }, []);
  if (pools == null) return <div className="py-8 grid place-items-center"><Loader2 size={18} className="text-charcoal/40 animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="business-engine-panel">
      {pools.map((p) => {
        const meta = POOL_META[p.key] || { label: p.label };
        return (
          <div key={p.key} className="border-2 border-charcoal rounded-xl bg-cream p-3 shadow-[2px_2px_0px_#1A1A1A]" data-testid={`engine-pool-${p.key}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal">{meta.label}</span>
              <span className="font-mono text-[10px] font-black text-charcoal tabular-nums">${(p.used ?? 0).toFixed(2)} / ${p.limit}</span>
            </div>
            <div className="h-3 w-full border-2 border-charcoal bg-white rounded-full overflow-hidden">
              <div className="h-full bg-gold" style={{ width: `${p.pct || 0}%` }} data-testid={`engine-bar-${p.key}`} />
            </div>
            <p className="mt-1 font-mono text-[8px] uppercase tracking-widest text-charcoal/45">{p.pct || 0}% of monthly ceiling used</p>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------- CONFIG -------------------------------- */
function ConfigPanel() {
  const [reg, setReg] = useState(null);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => adminApi.accessRegistry().then(setReg).catch(() => setReg({ pending: [], whitelist: [] }));
  useEffect(() => { load(); }, []);

  const addWhitelist = async () => {
    if (!val.trim()) { toast.error("Enter an email or token"); return; }
    setBusy(true);
    try { await adminApi.whitelist(val.trim()); toast.success("Whitelisted"); setVal(""); load(); }
    catch { toast.error("Failed to whitelist"); } finally { setBusy(false); }
  };
  const removeWl = async (v) => { try { await adminApi.whitelistRemove(v); load(); } catch { toast.error("Failed"); } };

  return (
    <div className="space-y-3" data-testid="business-config-panel">
      {/* Subscriber counter */}
      <div className="border-2 border-charcoal rounded-xl bg-charcoal p-3 flex items-center justify-between" data-testid="config-subscribers">
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-gold/70">Subscribers</p>
          <p className="font-serif text-3xl text-gold tabular-nums" data-testid="config-subscriber-count">{reg?.subscriber_count ?? "—"}</p>
        </div>
        <ShieldCheck size={26} className="text-gold" />
      </div>

      {/* Pending registrations */}
      <div className="border-2 border-charcoal rounded-xl bg-cream p-3">
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-charcoal/60 mb-2 flex items-center gap-1"><UserPlus size={12} /> Pending Registrations ({reg?.pending_count ?? 0})</p>
        <div className="space-y-1.5 max-h-32 overflow-y-auto no-scrollbar" data-testid="config-pending-list">
          {(reg?.pending || []).length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 py-2">No accounts awaiting sign-off.</p>
            : reg.pending.map((u, i) => (
              <div key={i} className="flex items-center justify-between border-2 border-charcoal/15 rounded-lg bg-white px-2 py-1.5" data-testid={`config-pending-${i}`}>
                <span className="font-mono text-[10px] text-charcoal truncate">{u.email || u.handle}</span>
                <button onClick={() => adminApi.whitelist(u.email).then(() => { toast.success("Approved"); load(); }).catch(() => toast.error("Failed"))}
                  data-testid={`config-approve-${i}`} className="shrink-0 font-mono text-[8px] font-black uppercase tracking-wide bg-gold text-black border-2 border-charcoal rounded-full px-2 py-0.5">Approve</button>
              </div>
            ))}
        </div>
      </div>

      {/* Bypass override */}
      <div className="border-2 border-charcoal rounded-xl bg-cream p-3">
        <p className="font-mono text-[9px] font-black uppercase tracking-[0.16em] text-charcoal/60 mb-2">Bypass Override</p>
        <div className="flex gap-2">
          <input value={val} onChange={(e) => setVal(e.target.value)} data-testid="config-bypass-input" placeholder="email or session token"
            className="flex-1 min-w-0 bg-white border-2 border-charcoal rounded-lg px-2.5 py-2 font-mono text-[11px] outline-none focus:ring-2 focus:ring-gold" />
          <button onClick={addWhitelist} disabled={busy} data-testid="config-bypass-btn" className="shrink-0 grid place-items-center bg-gold border-2 border-charcoal rounded-lg px-3 shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}</button>
        </div>
        {(reg?.whitelist || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="config-whitelist-chips">
            {reg.whitelist.map((w) => (
              <span key={w} className="flex items-center gap-1 bg-charcoal text-gold font-mono text-[8px] px-2 py-1 rounded-full border border-gold">
                {w}<button onClick={() => removeWl(w)} data-testid={`config-wl-remove-${w}`}><X size={9} /></button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------------------- TOOLS -------------------------------- */
function ToolsPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminApi.systemConfig().then(setCfg).catch(() => setCfg({ maintenance_mode: false, rate_limit_per_min: 20 })); }, []);

  const save = async (patch) => {
    setCfg((c) => ({ ...c, ...patch }));
    try { const r = await adminApi.setSystemConfig(patch); setCfg(r); }
    catch { toast.error("Save failed"); }
  };
  const wipe = async () => {
    setBusy(true);
    try { const r = await adminApi.wipeTemp(); toast.success(`Cleared ${r.scratchpads_removed} scratchpad(s)`); }
    catch { toast.error("Wipe failed"); } finally { setBusy(false); }
  };
  if (cfg == null) return <div className="py-8 grid place-items-center"><Loader2 size={18} className="text-charcoal/40 animate-spin" /></div>;
  const rl = cfg.rate_limit_per_min ?? 20;

  return (
    <div className="space-y-3" data-testid="business-tools-panel">
      {/* Maintenance toggle */}
      <div className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid="tools-maintenance">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal flex items-center gap-1"><Power size={12} /> Force System-Wide Maintenance</p>
            <p className="font-mono text-[8px] text-charcoal/45 mt-0.5">Blocks standard client generation routes.</p>
          </div>
          <button onClick={() => save({ maintenance_mode: !cfg.maintenance_mode })} data-testid="tools-maintenance-toggle"
            className={`shrink-0 w-14 h-8 rounded-full border-2 border-charcoal relative transition-colors ${cfg.maintenance_mode ? "bg-gold" : "bg-white"}`}>
            <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-charcoal transition-all ${cfg.maintenance_mode ? "left-[26px]" : "left-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Rate-limit modifier */}
      <div className="border-2 border-charcoal rounded-xl bg-cream p-3" data-testid="tools-ratelimit">
        <p className="font-mono text-[10px] font-black uppercase tracking-wide text-charcoal mb-2">Rate Limit · reqs / account / min</p>
        <div className="flex items-center gap-2">
          <button onClick={() => save({ rate_limit_per_min: Math.max(1, rl - 1) })} data-testid="tools-rl-minus" className="w-9 h-9 grid place-items-center bg-white border-2 border-charcoal rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><Minus size={15} /></button>
          <span className="flex-1 text-center font-serif text-2xl text-charcoal tabular-nums" data-testid="tools-rl-value">{rl}</span>
          <button onClick={() => save({ rate_limit_per_min: Math.min(600, rl + 1) })} data-testid="tools-rl-plus" className="w-9 h-9 grid place-items-center bg-white border-2 border-charcoal rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none"><Plus size={15} /></button>
        </div>
      </div>

      {/* Wipe temp logs — PURGE STUCK BUILDS master breaker */}
      <button onClick={wipe} disabled={busy} data-testid="tools-wipe-btn"
        className="w-full flex items-center justify-center gap-2 bg-red-600 text-white font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} 🚨 Purge Stuck Builds
      </button>
    </div>
  );
}

/* -------------------------------- CARD ---------------------------------- */
export default function BusinessCommandCard() {
  const [tab, setTab] = useState("payouts");
  return (
    <div className="border-4 border-charcoal rounded-2xl bg-white p-4 shadow-[4px_4px_0px_#1A1A1A]" data-testid="business-command-card">
      <p className="font-mono text-[9px] font-black uppercase tracking-[0.2em] text-charcoal/50 mb-3">Business</p>
      <div className="grid grid-cols-4 gap-2 mb-3" data-testid="business-tab-row">
        {TABS.map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`business-tab-${k}`}
            className={`flex flex-col items-center gap-1 rounded-xl border-2 border-charcoal py-2 font-mono text-[8px] font-black uppercase tracking-wider ${tab === k ? "bg-gold text-black shadow-[2px_2px_0px_#1A1A1A]" : "bg-white text-charcoal/50"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>
      <div className="max-h-[380px] overflow-y-auto no-scrollbar" data-testid="business-subcanvas">
        {tab === "payouts" && <PayoutsPanel />}
        {tab === "engine" && <EnginePanel />}
        {tab === "config" && <ConfigPanel />}
        {tab === "tools" && <ToolsPanel />}
      </div>
    </div>
  );
}
